import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { logout, useAuth } from '../sync/authStore';
import { createBoard, createInvite, deleteBoard, listBoards, updateBoard, type BoardSummary } from '../sync/boardsApi';
import { useUploads } from '../sync/uploadQueue';
import { IconTrash } from '../ui/icons';
import { api } from '../sync/api';
import { formatBytes } from '../board/items/FileItem';
import { paths } from './router';

export function Dashboard() {
  const user = useAuth((s) => s.user);
  const [boards, setBoards] = useState<BoardSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const pendingUploads = useUploads((s) => s.pending);
  const [usage, setUsage] = useState<{ total: number; boards: Record<string, number> } | null>(null);

  const refresh = useCallback(async () => {
    try {
      setBoards(await listBoards());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
    api
      .get<{ total: number; boards: { id: string; bytes: number }[] }>('/usage')
      .then((u) => setUsage({ total: u.total, boards: Object.fromEntries(u.boards.map((b) => [b.id, b.bytes])) }))
      .catch(() => setUsage(null));
  }, [refresh]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    const b = await createBoard(title.trim() || 'Untitled board');
    setTitle('');
    window.location.hash = paths.board(b.id);
  };

  const rename = async (b: BoardSummary) => {
    const next = prompt('Board title', b.title);
    if (next === null || next.trim() === b.title) return;
    await updateBoard(b.id, { title: next });
    void refresh();
  };

  const archive = async (b: BoardSummary, archived: boolean) => {
    await updateBoard(b.id, { archived });
    void refresh();
  };

  const remove = async (b: BoardSummary) => {
    if (!confirm(`Delete "${b.title}" and all its images? This cannot be undone.`)) return;
    await deleteBoard(b.id);
    void refresh();
  };

  const invite = async () => {
    const r = await createInvite();
    const url = `${window.location.origin}${window.location.pathname}${paths.register(r.code)}`;
    setInviteUrl(url);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* clipboard may be unavailable; the link is shown anyway */
    }
  };

  const visible = boards?.filter((b) => showArchived || !b.archived) ?? [];

  return (
    <main className="page">
      <header className="page-header">
        <h1 title="Visual Data Slate">VDS</h1>
        <div className="page-header-right">
          {pendingUploads > 0 && <span className="tag">{pendingUploads} uploads pending</span>}
          {usage && <span className="muted" title="Storage used by your images and attachments">{formatBytes(usage.total)}</span>}
          <span className="muted">{user?.name}</span>
          <button type="button" className="btn" onClick={invite}>
            Invite
          </button>
          <button type="button" className="btn" onClick={() => void logout()}>
            Sign out
          </button>
        </div>
      </header>

      {inviteUrl && (
        <div className="notice">
          <span>Invite link (valid 7 days, one use), copied to clipboard:</span>
          <code>{inviteUrl}</code>
          <button type="button" className="btn" onClick={() => setInviteUrl(null)}>
            Dismiss
          </button>
        </div>
      )}

      <section className="win">
        <div className="win-title">
          <span className="win-title-text">Boards</span>
          <label className="win-title-opt">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            archived
          </label>
        </div>
        <div className="win-body">
          <form className="row-form" onSubmit={onCreate}>
            <input placeholder="New board title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <button type="submit" className="btn btn-primary">
              New board
            </button>
          </form>

          {error && <div className="form-error">{error}</div>}
          {boards === null && !error && <p className="muted">Loading…</p>}
          {boards !== null && visible.length === 0 && <p className="muted">No boards yet. Create one above.</p>}

          <ul className="board-list">
            {visible.map((b) => (
              <li key={b.id} className={`board-row${b.archived ? ' is-archived' : ''}`}>
                <a className="board-row-title" href={paths.board(b.id)}>
                  {b.title}
                </a>
                <span className="muted board-row-meta">
                  {b.role} · {b.assetCount ?? 0} files{usage?.boards[b.id] ? ` (${formatBytes(usage.boards[b.id]!)})` : ''} · {formatDate(b.updatedAt)}
                </span>
                <span className="board-row-actions">
                  <button type="button" className="btn" onClick={() => rename(b)}>
                    Rename
                  </button>
                  <button type="button" className="btn" onClick={() => archive(b, !b.archived)}>
                    {b.archived ? 'Unarchive' : 'Archive'}
                  </button>
                  {b.role === 'owner' && (
                    <button type="button" className="btn danger" title="Delete" onClick={() => remove(b)}>
                      <IconTrash />
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <p className="muted page-foot">
        <a href={paths.local()}>Local scratch board</a> · stays in this browser, never uploaded
      </p>
    </main>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
